import { applyChanges, matchesNew } from '@components/bid_request/details/utils';
import { keepEqualHeaders } from '@features/buyer-line-items-pagination/utils/keep-equal-headers';
import { deepEqual } from '@utils/deep-equal';
import type { ID, Lot, LotChanges } from 'types';

interface MergeShellLotsParams {
  changes: Record<ID, LotChanges>;
  lots?: Lot[];
  originalLots: Lot[];
  shellLots: Lot[];
}

/**
 * The lots after a shell refresh: each lot takes the shell's details and headers and keeps the rows it holds,
 * a lot the shell brought starts with no rows, and a lot that is gone drops out. A lot whose details and
 * headers are unchanged keeps its objects, so its grid gets no new props.
 */
export const mergeShellLots = ({ changes, lots, originalLots, shellLots }: MergeShellLotsParams): { lots: Lot[]; originalLots: Lot[] } => {
  const previousById = new Map(originalLots.map(lot => [String(lot.id), lot]));
  const nextOriginalLots = shellLots.map((shellLot: Lot): Lot => {
    const previous = previousById.get(String(shellLot.id));
    if (!previous) return { ...shellLot, line_items: [] };

    const lot = keepEqualHeaders(previous, { ...shellLot, line_items: previous.line_items });

    return deepEqual(lot, previous) ? previous : lot;
  });
  const unchangedIds = new Set(nextOriginalLots
    .filter(lot => previousById.get(String(lot.id)) === lot)
    .map(lot => String(lot.id)));
  const previousLots = new Map((lots ?? []).map(lot => [String(lot.id), lot]));
  const nextLots = applyChanges({ lotChanges: changes, lots: nextOriginalLots }).map((lot: Lot): Lot => {
    const previous = previousLots.get(String(lot.id));
    if (!previous) return lot;
    if (unchangedIds.has(String(lot.id)) || matchesNew(String(lot.id))) return previous;

    return keepEqualHeaders(previous, lot);
  });

  return { lots: nextLots, originalLots: nextOriginalLots };
};
